import { useEffect, useState } from 'react';
import { geocode } from './api';
import type { Origin } from './eventFilters';

/** localStorage key for the last-used origin/radius. Shared across pages so
 *  setting an address on the events page also pre-fills the venues/series
 *  pages. Bump the suffix if the storage shape ever changes. */
export const ORIGIN_STORAGE_KEY = 'mhevents:origin:v1';

/** Default address used the very first time the site loads with no saved
 *  preference. Geocoded server-side via /api/geocode so we don't ship a
 *  Google API key to the browser. */
export const DEFAULT_ORIGIN_ADDRESS = '1257 Glen Laura Road';

interface StoredOrigin {
  origin: Origin;
  maxDistanceMiles?: number;
}

/** Returns the persisted origin (and radius) when the localStorage entry
 *  is present and well-formed, else null. */
export function loadSavedOrigin(): StoredOrigin | null {
  try {
    const raw = localStorage.getItem(ORIGIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredOrigin> | null;
    if (
      parsed &&
      parsed.origin &&
      typeof parsed.origin.lat === 'number' &&
      typeof parsed.origin.lng === 'number' &&
      typeof parsed.origin.label === 'string'
    ) {
      return {
        origin: parsed.origin,
        maxDistanceMiles:
          typeof parsed.maxDistanceMiles === 'number' && parsed.maxDistanceMiles > 0
            ? parsed.maxDistanceMiles
            : undefined,
      };
    }
  } catch {
    // Corrupted entry — drop it silently.
  }
  return null;
}

/** Best-effort persistence; ignores quota / private-mode failures. */
export function saveOriginToStorage(value: StoredOrigin | null): void {
  try {
    if (value && value.origin) {
      localStorage.setItem(ORIGIN_STORAGE_KEY, JSON.stringify(value));
    } else {
      localStorage.removeItem(ORIGIN_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

export interface UseStoredOriginResult {
  origin: Origin | undefined;
  maxDistanceMiles: number | undefined;
  /** Replace both fields at once. Pass `{}` (or both undefined) to clear. */
  setOriginState: (next: { origin?: Origin; maxDistanceMiles?: number }) => void;
}

/**
 * React hook that owns the user's distance-filter origin + radius and
 * persists it to localStorage. Falls back to geocoding {@link DEFAULT_ORIGIN_ADDRESS}
 * exactly once on first visit so users get a useful default without manual setup.
 *
 * Used by VenuesPublicPage / SeriesPublicPage; HomePage embeds the same state
 * inside its `FilterState` so it can also be encoded in the URL.
 */
export function useStoredOrigin(): UseStoredOriginResult {
  const [state, setState] = useState<StoredOrigin>(() => {
    const saved = loadSavedOrigin();
    return saved ?? { origin: undefined as unknown as Origin, maxDistanceMiles: undefined };
  });

  // First-visit fallback: kick off a geocode for the default address. Empty
  // deps = run once on mount.
  useEffect(() => {
    if (state.origin) return;
    let cancelled = false;
    void (async () => {
      const r = await geocode(DEFAULT_ORIGIN_ADDRESS).catch(() => null);
      if (cancelled || !r) return;
      const next: StoredOrigin = {
        origin: { label: DEFAULT_ORIGIN_ADDRESS, lat: r.lat, lng: r.lng },
        maxDistanceMiles: undefined,
      };
      setState((prev) => (prev.origin ? prev : next));
      saveOriginToStorage(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOriginState = (next: { origin?: Origin; maxDistanceMiles?: number }) => {
    const merged: StoredOrigin = next.origin
      ? { origin: next.origin, maxDistanceMiles: next.maxDistanceMiles }
      : { origin: undefined as unknown as Origin, maxDistanceMiles: undefined };
    setState(merged);
    saveOriginToStorage(merged.origin ? merged : null);
  };

  return {
    origin: state.origin || undefined,
    maxDistanceMiles: state.maxDistanceMiles,
    setOriginState,
  };
}
